import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';

/**
 * The custom command editor.
 *
 * The actions are repeatable rows, read through `data-field` relative to their
 * own row. An indexed name would break when the middle action is deleted, and
 * the command would save the wrong actions with nothing saying so.
 */

interface CustomCommand {
  _id: string;
  name: string;
  description: string;
  enabled: boolean;
  actions: Record<string, unknown>[];
  requiredArgs: number;
  allowedChannelIds: string[];
  blockedChannelIds: string[];
  allowedRoleIds: string[];
  blockedRoleIds: string[];
  guildCooldownSeconds: number;
  userCooldownSeconds: number;
  deleteTrigger: boolean;
  uses: number;
}

interface EditorPageData {
  command: CustomCommand;
  guildId: string;
}

/** Editing a command, on a page of its own. */
export function initCommandsEditorPage(): void {
  const form = $<HTMLFormElement>('[data-command-form]');
  if (!form) return;

  const { command, guildId } = pageData<EditorPageData>();

  wireActions(form);

  fillForm(form, command as unknown as Record<string, unknown>);
  rebuild(form, command.actions);

  for (const field of [
    'allowedChannelIds',
    'blockedChannelIds',
    'allowedRoleIds',
    'blockedRoleIds',
  ] as const) {
    const menu = findSelectMenu(form, field);
    if (menu) menu.value = command[field];
  }

  // `enabled` defaults to true: a command somebody has just written should
  // work without their having to think of switching it on.
  const enabled = form.elements.namedItem('enabled');
  if (enabled instanceof HTMLInputElement) enabled.checked = command.enabled;

  const trigger = form.elements.namedItem('deleteTrigger');
  if (trigger instanceof HTMLInputElement) trigger.checked = command.deleteTrigger;

  mountResourceEditor({
    endpoint: 'commands',
    listUrl: `/modules/commands?guildId=${guildId}`,
    guildId,
    form,
    itemId: command._id,

    serialize: () => ({
      name: text(form, 'name'),
      description: text(form, 'description'),
      enabled: checked(form, 'enabled'),

      actions: $$<HTMLElement>('[data-action-row]', form).map(readAction),

      requiredArgs: number(form, 'requiredArgs'),
      allowedChannelIds: findSelectMenu(form, 'allowedChannelIds')?.value ?? [],
      blockedChannelIds: findSelectMenu(form, 'blockedChannelIds')?.value ?? [],
      allowedRoleIds: findSelectMenu(form, 'allowedRoleIds')?.value ?? [],
      blockedRoleIds: findSelectMenu(form, 'blockedRoleIds')?.value ?? [],

      guildCooldownSeconds: number(form, 'guildCooldownSeconds'),
      userCooldownSeconds: number(form, 'userCooldownSeconds'),
      deleteTrigger: checked(form, 'deleteTrigger'),
      publish: false,
    }),

    labels: {
      saved: ct('commandSaved', 'Command saved.'),
      confirmDelete: ct('confirmDeleteCommand', 'Delete this command?'),
      deleted: ct('commandDeleted', 'Command deleted.'),
    },
  });
}

function readAction(row: HTMLElement): Record<string, unknown> {
  return {
    type: choice(row, 'type', 'send_message'),
    templateName: value(row, 'templateName'),
    content: value(row, 'content'),
    contentPool: [],
    roleId: findSelectMenu(row, 'roleId')?.single ?? '',
    emoji: value(row, 'emoji'),
    ephemeral: false,
    deleteAfterSeconds: Number(value(row, 'deleteAfterSeconds')) || 0,
  };
}

/* -------------------------------------------------------------------- lignes */

let added = 0;

function wireActions(form: HTMLFormElement): void {
  form.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    if (target.closest('[data-add="action"]')) {
      addAction(form);
      return;
    }

    const remove = target.closest('[data-remove]');
    if (remove) {
      remove.closest('[data-action-row]')?.remove();
      refreshEmpty(form);
      form.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function addAction(form: HTMLFormElement): HTMLElement | null {
  const blueprint = $<HTMLTemplateElement>('[data-template="action"]');
  const list = $<HTMLElement>('[data-list="action"]', form);
  if (!blueprint || !list) return null;

  added += 1;

  const holder = document.createElement('div');
  holder.innerHTML = blueprint.innerHTML.replaceAll('__ROW__', `n${String(added)}`);

  const node = holder.firstElementChild;
  if (!(node instanceof HTMLElement)) return null;

  list.append(node);

  // The row template's role menu is still native: it lived inside a
  // `<template>`, which nothing walked at startup.
  upgradeSelects(document);

  refreshEmpty(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));
  return node;
}

function rebuild(form: HTMLFormElement, actions: Record<string, unknown>[]): void {
  const list = $<HTMLElement>('[data-list="action"]', form);
  if (!list) return;

  list.replaceChildren();

  for (const action of actions) {
    const row = addAction(form);
    if (!row) continue;

    setChoice(row, 'type', str(action.type) || 'send_message');
    setValue(row, 'templateName', str(action.templateName));
    setValue(row, 'content', str(action.content));
    setValue(row, 'emoji', str(action.emoji));
    setValue(row, 'deleteAfterSeconds', str(action.deleteAfterSeconds));

    const role = findSelectMenu(row, 'roleId');
    if (role) role.value = action.roleId ? [str(action.roleId)] : [];
  }

  refreshEmpty(form);
}

function refreshEmpty(form: HTMLFormElement): void {
  const note = $<HTMLElement>('[data-empty="action"]', form);
  const list = $<HTMLElement>('[data-list="action"]', form);
  if (note) note.hidden = (list?.children.length ?? 0) > 0;
}

/* ------------------------------------------------------------------- champs */

/** A value from the server, reduced to a string with no "[object Object]". */
function str(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function field(root: ParentNode, name: string): Element | null {
  return root.querySelector(`[data-field="${CSS.escape(name)}"]`);
}

/**
 * A row field's value, whatever kind of control carries it.
 *
 * `<select>` is in the list because the template picker is one — a native one,
 * deliberately: it carries no `data-select-menu`, so `upgradeSelects` leaves it
 * alone and cloning the row template keeps its options.
 */
function value(root: ParentNode, name: string): string {
  const node = field(root, name);
  if (node instanceof HTMLSelectElement) return node.value;

  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function setValue(root: ParentNode, name: string, next: string): void {
  const node = field(root, name);
  if (node instanceof HTMLSelectElement) {
    node.value = next;
    return;
  }

  if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) node.value = next;
}

/** The checked value of a card group, read through its wrapper. */
function choice(root: ParentNode, name: string, fallback: string): string {
  const wrapper = field(root, name);
  const ticked = wrapper?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  return ticked?.value || fallback;
}

function setChoice(root: ParentNode, name: string, next: string): void {
  const wrapper = field(root, name);
  const target = wrapper?.querySelector<HTMLInputElement>(
    `input[type="radio"][value="${CSS.escape(next)}"]`,
  );
  if (target) target.checked = true;
}

function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const parsed = node instanceof HTMLInputElement ? Number(node.value) : 0;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}
